import { z } from 'zod'
import { formaterJour } from './dates'
import { heureAParis, jourAParis } from './calendrier'
import type { StatutCollecte, StatutDefi, TypeDefi } from './domaine'
import { caviarder, hoteAutorise, LIBELLES_DEFI } from './portails'

// Notifications de l'appli installable (Web Push) : règles pures. Un abonnement désigne un
// appareil d'une personne chez son service de notification (Apple, Google, Mozilla, Microsoft) ;
// le serveur n'écrit qu'à ces services, jamais à une adresse choisie par le navigateur. Une
// notification de défi dit quelle connexion attend, ce qui est demandé et jusqu'à quand : jamais
// un code, un identifiant, un nom de secret ni une adresse du portail.

/** Services de notification des navigateurs ; une entrée qui commence par un point couvre ses sous-domaines. */
export const SERVICES_PUSH = [
  'fcm.googleapis.com',
  '.push.apple.com',
  '.push.services.mozilla.com',
  '.notify.windows.com',
] as const

/** Adresse d'un abonnement : https, port par défaut, chez un service de notification connu. */
export function endpointPushPermis(endpoint: string): boolean {
  let url: URL
  try {
    url = new URL(endpoint)
  } catch {
    return false
  }
  if (url.protocol !== 'https:' || url.port !== '' || url.username || url.password) return false
  return hoteAutorise(url.hostname, SERVICES_PUSH)
}

const BASE64URL = /^[A-Za-z0-9_-]+={0,2}$/

export const abonnementPush = z
  .object({
    endpoint: z.string().max(2048).refine(endpointPushPermis, 'Service de notification inconnu.'),
    cles: z
      .object({
        /** Clé publique P-256 du navigateur (65 octets). */
        p256dh: z.string().min(86).max(90).regex(BASE64URL),
        /** Secret d'authentification (16 octets). */
        auth: z.string().min(21).max(24).regex(BASE64URL),
      })
      .strict(),
    libelle: z.string().trim().min(1).max(80),
  })
  .strict()
export type AbonnementPush = z.infer<typeof abonnementPush>

/** « iPhone · Safari » : proposé par l'appli au moment de l'abonnement. */
export function libelleAppareil(userAgent: string): string {
  const ua = userAgent
  const systeme = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Macintosh|Mac OS X/.test(ua)
          ? 'Mac'
          : /Windows/.test(ua)
            ? 'Windows'
            : /Linux/.test(ua)
              ? 'Linux'
              : null
  const navigateur = /Edg(A|iOS)?\//.test(ua)
    ? 'Edge'
    : /Firefox\/|FxiOS/.test(ua)
      ? 'Firefox'
      : /Chrome\/|CriOS/.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : null
  return [systeme, navigateur].filter(Boolean).join(' · ') || 'Appareil'
}

const RETIRES: readonly RegExp[] = [
  /\b[a-z][a-z0-9+.-]*:\/\/\S*/gi,
  /\S+@\S+/g,
  /\bPORTAIL_[A-Z0-9_]*/g,
  /\b(?:[a-z0-9-]+\.)+[a-z]{2,}\b/gi,
  /\d[\d\s-]{2,}\d/g,
]

/**
 * Texte prêt pour une notification (écran verrouillé) : sans adresse, courriel, nom de secret,
 * suite de chiffres (un code) ni jeton ; borné.
 */
export function texteSansDonnee(texte: string, max = 120): string {
  let t = caviarder(texte, []).replace(/\[masqué\]/g, '')
  for (const motif of RETIRES) t = t.replace(motif, '')
  t = t
    .replace(/\s+([,.;:)])/g, '$1')
    .replace(/\(\s*\)/g, '')
    .replace(/[\s,;:]+$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
  return t.length > max ? `${t.slice(0, max - 1)}…` : t
}

export type NotificationDefi = {
  titre: string
  corps: string
  /** Une notification remplace la précédente du même défi. */
  tag: string
  /** Seule donnée de la notification : l'écran du défi. */
  url: string
  /** Rester affichée jusqu'au geste (défi en direct). */
  exigeInteraction: boolean
  /** Sans son ni vibration (défi à relancer, souvent la nuit). */
  silencieuse: boolean
  /** Nombre à poser sur l'icône de l'appli (tâches nouvelles) ; absent, l'icône ne change pas. */
  pastille?: number
}

const sansPointFinal = (t: string) => t.replace(/[.\s]+$/, '')
const majuscule = (t: string) => t.charAt(0).toLocaleUpperCase('fr-FR') + t.slice(1)

export function notificationDefi(d: {
  id: string
  libelle: string
  type: TypeDefi
  message: string | null
  enDirect: boolean
  expireLe: Date
}): NotificationDefi {
  const libelle = texteSansDonnee(d.libelle, 60) || 'Portail'
  const message = d.message ? sansPointFinal(texteSansDonnee(d.message)) : ''
  const demande = majuscule(message || LIBELLES_DEFI[d.type])
  const corps = d.enDirect
    ? `${demande}. Avant ${heureAParis(d.expireLe)}.`
    : `${majuscule(LIBELLES_DEFI[d.type])} : connexion à relancer avant le ${formaterJour(jourAParis(d.expireLe))}.`
  return {
    titre: `Vérification : ${libelle}`,
    corps,
    tag: `defi-${d.id}`,
    url: `/defi/${d.id}`,
    exigeInteraction: d.enDirect,
    silencieuse: !d.enDirect,
  }
}

export type EtapeDefi =
  'attente' | 'connexion' | 'collecte' | 'terminee' | 'expiree' | 'abandonnee' | 'refusee' | 'echec'

const pluriel = (n: number, mot: string, motPluriel = `${mot}s`) =>
  `${n} ${n > 1 ? motPluriel : mot}`

/** Ce que l'écran du défi dit de la suite : le défi, puis la collecte qui le porte. */
export function issueDefi(e: {
  statut: StatutDefi
  repondu: boolean
  /** Faux : connexion de nuit, que « Lancer la connexion » relance (le portail redemandera). */
  enDirect: boolean
  collecte: { statut: StatutCollecte; documentsTrouves: number; documentsNouveaux: number }
}): { etape: EtapeDefi; texte: string } {
  const c = e.collecte
  switch (c.statut) {
    case 'terminee':
      return {
        etape: 'terminee',
        texte:
          c.documentsTrouves === 0
            ? 'Connecté : aucun document trouvé.'
            : `Connecté : ${pluriel(c.documentsTrouves, 'document trouvé', 'documents trouvés')}, ${pluriel(c.documentsNouveaux, 'nouveau', 'nouveaux')}.`,
      }
    case 'bloquee':
      return { etape: 'refusee', texte: 'Refusé : la collecte est bloquée.' }
    case 'echec':
      return { etape: 'echec', texte: 'Échec de la collecte.' }
    case 'annulee':
      return { etape: 'abandonnee', texte: 'Annulée : la collecte a été retirée.' }
    case 'expiree':
      return e.statut === 'annule'
        ? { etape: 'abandonnee', texte: 'Abandonné : la connexion reste à vérifier.' }
        : { etape: 'expiree', texte: 'Expiré : personne n’a répondu à temps.' }
    default:
      break
  }
  if (e.statut === 'en_attente')
    return e.repondu
      ? { etape: 'connexion', texte: 'Code transmis, connexion en cours…' }
      : { etape: 'attente', texte: 'En attente de votre réponse.' }
  if (e.statut === 'expire') return { etape: 'expiree', texte: 'Expiré.' }
  if (e.statut === 'annule') return { etape: 'abandonnee', texte: 'Abandonné.' }
  if (!e.enDirect)
    return {
      etape: 'connexion',
      texte: 'Connexion lancée, le portail va demander la vérification…',
    }
  return { etape: 'collecte', texte: 'Connecté, collecte en cours…' }
}
